'use client';

/**
 * 오가닉 카드 계정의 얼굴·팔로워 — **두 원장을 겹쳐** 돌려준다.
 *
 * ```
 *   ① 발굴 색인 (`docs.byIds`)          공짜다 — 먼저 묻는다
 *   ② 실시간 조회 창구 (`/api/profiles`) ①에서 못 찾은 계정만 · 유료다
 * ```
 * 불러온 오가닉 글의 계정을 모아 한 번에 묻는다. 페이지가 붙으면 **새로 생긴 id 만** 묻고(받은 지 `STALE_MS` 안의 것은
 * 다시 묻지 않는다), 받는 동안은 앞 결과를 쥐고 있는다.
 * 🔑 지도는 전 id 를 한 번에 물은 것과 같다 — 받은 문서를 id 마다 쥐어 두고 정렬된 id 차례로 다시 읽는다
 *   (대소문자만 다른 두 문서가 같은 계정 열쇠로 접힐 때 뒤엣것이 이기는 규칙까지 같다).
 *
 * 🔑 **①이 못 채우는 자리가 훨씬 넓다** — 실측(메디힐 틱톡 90일 상위 20계정) 색인에 있는 것은 **1개**였다.
 *   나머지는 「얼굴이 없다」가 아니라 «계정 문서가 아예 없다»라, ②가 그 자리에서 받아 채운다.
 * ⚠️ ②는 아껴 쓰는 규칙이 있다 — `use-account-profile-lookup` 머리말이 정본이다.
 * ⚠️ 둘 다 못 찾은 계정은 비어 있다 — 부르는 쪽이 **머리글자**로 떨어뜨린다(대신 만들지 않는다).
 */

import { useRef, useMemo } from 'react';
import { useQuery, keepPreviousData } from '@tanstack/react-query';

import { searchAllKeys } from 'src/query-keys/search-all';
import { fetchInfluencerDocsByIds } from 'src/actions/els/influencer';

import { useAccountProfileLookup } from './use-account-profile-lookup';
import { overlayProfiles } from '../../../utils/account-profile-overlay';
import { toProfileDocIds, toProfileAccounts, readAccountProfiles } from '../../../utils/search-all-media-cards';

import type { AccountProfile } from '../../../utils/search-all-media-cards';

const STALE_MS = 5 * 60 * 1000;

const EMPTY = new Map<string, AccountProfile>();

/** @param posts 쌓인 오가닉 글(ingest 됨) */
export function useSearchAllAccountProfiles(posts: Record<string, unknown>[]): Map<string, AccountProfile> {
  const ids = useMemo(() => toProfileDocIds(posts), [posts]);
  const idsKey = ids.join('|');

  /** 받은 계정 문서 — id 마다(없던 id 는 `null`)와 받은 때 */
  const received = useRef(new Map<string, { row: unknown; at: number }>());

  const query = useQuery({
    queryKey: searchAllKeys.accountProfiles(idsKey),
    enabled: ids.length > 0,
    staleTime: STALE_MS,
    placeholderData: keepPreviousData,
    queryFn: async () => {
      const now = Date.now();
      const store = received.current;
      const missing = ids.filter((id) => {
        const hit = store.get(id);
        return !hit || now - hit.at >= STALE_MS;
      });
      if (missing.length > 0) {
        const { rows } = await fetchInfluencerDocsByIds({ ids: missing });
        const byId = new Map(rows.map((row) => [docIdOf(row), row]));
        missing.forEach((id) => store.set(id, { row: byId.get(id) ?? null, at: now }));
      }
      return readAccountProfiles(ids.map((id) => store.get(id)?.row).filter((row) => row != null));
    },
  });

  const ledger = query.data ?? EMPTY;
  const accounts = useMemo(() => toProfileAccounts(posts), [posts]);
  const filled = useAccountProfileLookup(accounts, ledger);

  // 색인이 먼저다 — 창구는 **빈자리만** 메운다(색인 값을 덮지 않는다)
  return useMemo(() => overlayProfiles(ledger, filled), [ledger, filled]);
}

/** 서버가 돌려준 문서의 id — 서버가 이 값으로 묻은 id 와 짝짓는다(`influencer-list` 의 `fetchInfluencerDocsByIds`) */
function docIdOf(row: unknown): string {
  const id = row != null && typeof row === 'object' ? (row as { id?: unknown }).id : '';
  return String(id ?? '');
}
